

import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { Arc, Character } from '@/lib/types';

import { useNovel } from '@/context/novel-context';
import { useFirebase } from '@/firebase';
import { useToast } from '@/hooks/use-toast';
import { arcCoreSchema } from '@/lib/schemas/arc';

interface QuickArcDialogProps {
  open: boolean;
  onClose: () => void;
  characters: Character[];
}

function generateId(): string {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  let autoId = '';
  for (let i = 0; i < 20; i++) {
    autoId += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return autoId;
}

export function QuickArcDialog({
  open,
  onClose,
  characters,
}: QuickArcDialogProps) {
  const { backend } = useFirebase();
  const { novel } = useNovel();
  const { toast } = useToast();

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [type, setType] = useState<'plot' | 'character'>('plot');
  const [selectedCharacter, setSelectedCharacter] = useState<Character | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const { t } = useTranslation();

  const handleSubmit = async () => {
    if (!novel.id) return;

    // Validate
    const validation = arcCoreSchema.safeParse({
      name,
      description,
      type,
      characterId: type === 'character' ? selectedCharacter?.id : undefined,
    });

    if (!validation.success) {
      toast({
        title: t('arcs.validation_error'),
        description: validation.error.errors[0].message,
        variant: 'destructive',
      });
      return;
    }

    setIsSaving(true);
    try {
      const arcId = generateId();

      const arcData: Arc = {
        id: arcId,
        name: name.trim(),
        description: description.trim(),
        shortDescription: '',
        images: [],
        primaryImageId: null,
        type,
        characterId: type === 'character' ? (selectedCharacter?.id || undefined) : undefined,
        narrativeStatus: 'notWritten',
        order: Date.now(),
        audit: {
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        },
      };

      await backend.narrative.createArc(novel.ownerId, novel, arcData);

      toast({
        title: t('arcs.success'),
        description: t('arcs.arc_created'),
      });
      onClose();
      // Reset form
      setName('');
      setDescription('');
      setType('plot');
      setSelectedCharacter(null);
    } catch (error) {
      console.error('Error creating arc:', error);
      toast({
        title: t('arcs.error'),
        description: t('arcs.arc_create_failed'),
        variant: 'destructive',
      });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle sx={{ fontWeight: 700 }}>{t('arcs.new_arc')}</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, pt: 1 }}>
          <TextField
            label={t('arcs.arc_name')}
            value={name}
            onChange={(e) => setName(e.target.value)}
            fullWidth
            required
            autoFocus
          />

          <TextField
            label={t('arcs.description')}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            fullWidth
            multiline
            rows={3}
          />

          <FormControl fullWidth>
            <InputLabel>{t('arcs.type')}</InputLabel>
            <Select
              value={type}
              label={t('arcs.type')}
              onChange={(e) => setType(e.target.value as 'plot' | 'character')}
            >
              <MenuItem value="plot">{t('arcs.filter_plot_arc')}</MenuItem>
              <MenuItem value="character">{t('arcs.filter_character_arc')}</MenuItem>
            </Select>
          </FormControl>

          {type === 'character' && (
            <Autocomplete
              options={characters}
              getOptionLabel={(option) => option.name}
              value={selectedCharacter}
              onChange={(_, newValue) => setSelectedCharacter(newValue)}
              renderInput={(params) => (
                <TextField {...params} label={t('arcs.select_character')} required />
              )}
              fullWidth
            />
          )}

          {type === 'character' && !selectedCharacter && (
            <Typography variant="caption" color="error">
              {t('arcs.select_character_required')}
            </Typography>
          )}
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={isSaving}>
          {t('arcs.cancel')}
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={isSaving || (type === 'character' && !selectedCharacter) || !name.trim()}
        >
          {isSaving ? t('arcs.creating') : t('arcs.create_arc')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

